import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { BdPageable } from '@bindoc-projects/platform-lib/data';
import { BdCompany } from '@bindoc-projects/resource-domain-lib';
import { BdAuthenticationService } from '@bindoc-projects/security-lib/data';
import { BdDataTestDirective } from '@bindoc-projects/util-lib/ui';
import { OverlayOptions } from 'primeng/api';
import { IftaLabel } from 'primeng/iftalabel';
import { Select, SelectFilterEvent, SelectLazyLoadEvent } from 'primeng/select';
import { filter, switchMap, take, tap } from 'rxjs';

import { BdCompanyContextService } from './company-context.service';
import { BdShareablesService } from '../resources/core/resources.service';

/**
 * Filler for not-yet-loaded slots in the virtual-scroll buffer. It MUST be a
 * non-null object: PrimeNG's `Select` calls `isOptionGroup(option)` over the
 * whole options array (aria sizing) without guarding null/undefined, and
 * `optionGroupLabel` defaults to a truthy value, so a `null` slot throws
 * `Cannot read properties of null (reading 'optionGroup')`. Marked disabled so
 * it can neither be focused nor selected while its page is loading.
 */
interface CompanyPlaceholder {
  id: string;
  name: string;
  disabled: true;
}

type CompanyOption = BdCompany | CompanyPlaceholder;

/**
 * Global company/customer context selector rendered in the app header.
 *
 * Companies are loaded **lazily** from the paginated `GET /companies/`
 * endpoint: only the page(s) scrolled into view are fetched (server-side
 * `sort=name` + `query`), so users with many companies no longer trigger an
 * eager download of the full list. The empty (cleared) state represents
 * "Alle Kunden" — clearing the selection resets the context to all customers.
 */
@Component({
  selector: 'bd-company-context-selector',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <p-iftalabel>
      <p-select
        #companySelect
        [inputId]="selectId"
        [formControl]="selectCtrl"
        [options]="options()"
        [data-test]="selectId"
        fluid
        variant="filled"
        class="mb-4"
        dataKey="id"
        optionLabel="name"
        [placeholder]="allCompaniesLabel"
        [loading]="loading()"
        [showClear]="true"
        optionDisabled="disabled"
        [virtualScroll]="true"
        [virtualScrollItemSize]="38"
        [lazy]="true"
        [filter]="true"
        filterBy="name"
        [filterPlaceholder]="filterPlaceholder"
        [emptyFilterMessage]="emptyMessage"
        [emptyMessage]="emptyMessage"
        [overlayOptions]="overlayOptions"
        (onShow)="ensureInitialLoad()"
        (onLazyLoad)="onLazyLoad($event)"
        (onFilter)="onFilter($event)"
        (onChange)="onSelection($event.value)"
      ></p-select>
      <label [for]="selectId">Kunde</label>
    </p-iftalabel>
  `,
  imports: [ReactiveFormsModule, IftaLabel, Select, BdDataTestDirective],
})
export class BdCompanyContextSelectorComponent implements OnInit {
  private static readonly PAGE_SIZE = 50;

  private readonly _companyContext = inject(BdCompanyContextService);
  private readonly _resourcesService = inject(BdShareablesService);
  private readonly _authService = inject(BdAuthenticationService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _injector = inject(Injector);
  private readonly _select = viewChild<Select>('companySelect');

  public readonly selectId = 'company-context-selector';
  public readonly allCompaniesLabel = 'Alle Kunden';
  public readonly filterPlaceholder = 'Kunde suchen';
  public readonly emptyMessage = 'Keine Kunden gefunden';

  public readonly overlayOptions: OverlayOptions = {
    mode: 'overlay',
    appendTo: 'body',
  };

  private static readonly PLACEHOLDER: CompanyPlaceholder = {
    id: '',
    name: '…',
    disabled: true,
  };

  public readonly selectCtrl = new FormControl<BdCompany | null>(null);
  public readonly options = signal<CompanyOption[]>([]);
  public readonly loading = signal(false);

  /** Persistent sparse buffer, indexed by the company's global position. */
  private _buffer: CompanyOption[] = [];
  private _total = 0;
  private _query = '';
  private readonly _loadedPages = new Set<number>();
  private readonly _optionCount = computed(() => this.options().length);

  constructor() {
    // PrimeNG 22's Scroller no longer re-initialises in lazy mode when its
    // items change (`if (this.initialized && !this._lazy()) this.init()`), so a
    // panel opened before the first page arrived keeps rendering the empty
    // window it computed then — a blank list until it is closed and reopened
    // (BDA-6313). Re-init it whenever the option count changes, after the new
    // options have reached the scroller, as PrimeNG 21 did on its own.
    effect(() => {
      this._optionCount();
      afterNextRender(() => this._reinitScroller(), {
        injector: this._injector,
      });
    });
  }

  ngOnInit(): void {
    // Restore the previously selected company (from storage, no fetch) and
    // mirror it into the control so the header shows the active context
    // without loading the list.
    //
    // Gate on `accountInfo$` — NOT `authentication$` — because the selection
    // is persisted under a user-scoped storage key
    // (`bd.company-context.selected-company.<userId>`). `_userId` is derived
    // from `accountInfo$`, which `BdAuthenticationService` emits *after*
    // `authentication$` within the same synchronous MSAL callback. Restoring
    // on `authentication$` therefore ran while `_userId` was still undefined,
    // read the wrong (base) key and never recovered (BDA-5654). Waiting for a
    // truthy account guarantees `BdCompanyContextService` — which subscribes
    // to `accountInfo$` in its constructor, i.e. before this component — has
    // already set `_userId`, so store and restore share the same key.
    this._authService.msalInitialized
      .pipe(
        switchMap(() => this._authService.accountInfo$),
        filter((accountInfo) => !!accountInfo),
        take(1),
        tap(() => this._companyContext.restoreSelection()),
        switchMap(() => this._companyContext.selectedCompany$.pipe(take(1))),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((company) => {
        this.selectCtrl.setValue(company ?? null, { emitEvent: false });
        // Seed the options with the selected company so p-select can render
        // its label immediately — PrimeNG only shows a value's label when the
        // value is present in `options`, and the list is loaded lazily.
        if (company) {
          this.options.set([company]);
        }
      });
  }

  /** First panel open: pull the first page so the virtual scroller can size. */
  public ensureInitialLoad(): void {
    if (this._loadedPages.size === 0) {
      this._loadPage(0);
    }
  }

  public onLazyLoad(event: SelectLazyLoadEvent): void {
    const first = event.first ?? 0;
    const last = event.last ?? first;
    const size = BdCompanyContextSelectorComponent.PAGE_SIZE;
    const startPage = Math.floor(first / size);
    const endPage = Math.floor(Math.max(first, last - 1) / size);
    for (let page = startPage; page <= endPage; page++) {
      this._loadPage(page);
    }
  }

  public onFilter(event: SelectFilterEvent): void {
    const query = (event.filter ?? '').toString().trim();
    if (query === this._query) {
      return;
    }
    this._query = query;
    this._resetBuffer();
    this._loadPage(0);
  }

  public onSelection(company: CompanyOption | null | undefined): void {
    // A real company always carries a non-empty id; the cleared state and the
    // (disabled) loading placeholder map back to "Alle Kunden".
    const selected = company && company.id ? (company as BdCompany) : undefined;
    this._companyContext.selectCompany(selected);
  }

  private _loadPage(page: number): void {
    if (this._loadedPages.has(page)) {
      return;
    }
    this._loadedPages.add(page);
    // Snapshot the query so a response for a stale filter is discarded.
    const query = this._query;
    this.loading.set(true);

    this._resourcesService
      .searchMyCompanies(
        page,
        BdCompanyContextSelectorComponent.PAGE_SIZE,
        query,
      )
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (response) => {
          if (query === this._query) {
            this._applyPage(response);
          }
          this.loading.set(false);
        },
        error: () => {
          // Allow the page to be retried on the next scroll.
          this._loadedPages.delete(page);
          this.loading.set(false);
        },
      });
  }

  private _applyPage(response: BdPageable<BdCompany>): void {
    if (this._total !== response.totalElements) {
      this._total = response.totalElements;
      this._buffer = new Array<CompanyOption>(response.totalElements).fill(
        BdCompanyContextSelectorComponent.PLACEHOLDER,
      );
    }
    const start = response.number * BdCompanyContextSelectorComponent.PAGE_SIZE;
    response.content.forEach((company, index) => {
      this._buffer[start + index] = company;
    });
    // Emit a fresh array reference so OnPush picks up the change.
    this.options.set([...this._buffer]);
  }

  private _reinitScroller(): void {
    const scroller = this._select()?.scroller();
    if (scroller?.initialized) {
      scroller.init();
    }
  }

  private _resetBuffer(): void {
    this._loadedPages.clear();
    this._buffer = [];
    this._total = 0;
    this.options.set([]);
  }
}
